/**
 * Animal Park journey upsells: Castle & Gardens entry (step 1 or upsell) unlocks
 * the Animal Park Free – Promotion upsell, same as choosing Animal Park as the main ticket.
 */
export function ticketLineIsCastleAndGardenEntry(name) {
  const n = (name || '').toLowerCase();
  return n.includes('castle and garden entry');
}

/** Basket row may put the upsell card title on parentUpsellName and the SKU on name */
export function ticketRowIsCastleAndGardenEntry(ticket) {
  if (!ticket) return false;
  const combined = `${ticket.name || ''} ${ticket.parentUpsellName || ''}`;
  return ticketLineIsCastleAndGardenEntry(combined);
}

/**
 * Garden & Playground entry tickets (all variants) — also qualify for the free
 * Animal Park promotion wherever an Animal Park upsell is available.
 * Matches: "Garden and Playground Entry", "Garden and Playground Only Entry",
 * "Garden and Adventure Playground Only Ticket", Friends of Belvoir garden lines,
 * and RHS Member garden lines.
 */
export function ticketLineIsGardenAndPlaygroundEntry(name) {
  const n = (name || '').toLowerCase();
  if (n.includes('garden and playground')) return true;
  if (n.includes('garden and adventure playground')) return true;
  if (n.includes('friends of belvoir') && n.includes('garden')) return true;
  return false;
}

export function ticketRowIsGardenAndPlaygroundEntry(ticket) {
  if (!ticket) return false;
  const combined = `${ticket.name || ''} ${ticket.parentUpsellName || ''}`;
  return ticketLineIsGardenAndPlaygroundEntry(combined);
}

/**
 * Garden & Playground–only entry (not Castle & Gardens). Mutually exclusive with castle lines in basket UI.
 */
export function ticketRowIsGardenPlaygroundOnlyEntryBasket(ticket) {
  if (!ticket) return false;
  const qty = parseInt(ticket.quantity, 10) || 0;
  if (qty <= 0) return false;
  if (ticketRowIsCastleAndGardenEntry(ticket)) return false;
  const combined = `${ticket.name || ''} ${ticket.parentUpsellName || ''}`.toLowerCase();
  return (
    combined.includes('garden and playground') ||
    combined.includes('gardens and playground') ||
    (combined.includes('garden') && combined.includes('playground') && combined.includes('only'))
  );
}

/**
 * Historic Houses Association (HHA) member Castle & Gardens lines — not eligible for the
 * “Animal Park Free – Promotion” offer; those visitors add paid Animal Park instead.
 */
export function ticketLineLooksLikeHhaHistoricHouseMember(text) {
  const n = (text || '').toLowerCase();
  if (
    n.includes('historic house member') ||
    n.includes('historic houses member') ||
    n.includes('historic houses association') ||
    n.includes('hha member') ||
    n.includes('hha pass') ||
    n.includes('bring your hha')
  ) {
    return true;
  }
  // Short Venpos titles sometimes use "HHA" without the full phrase (still member pricing)
  if (n.includes('hha') && (n.includes('member') || n.includes('historic house'))) {
    return true;
  }
  return false;
}

export function ticketRowIsHhaHistoricHouseMember(ticket) {
  if (!ticket) return false;
  const combined = `${ticket.name || ''} ${ticket.description || ''} ${ticket.parentUpsellName || ''}`;
  return ticketLineLooksLikeHhaHistoricHouseMember(combined);
}

/**
 * Quantity of Castle & Gardens or Garden & Playground tickets that qualify for the
 * free Animal Park promotion (excludes HHA member lines).
 */
export function castleAndGardenPaidQtyForFreeAnimalParkPromotion(selectedTickets) {
  return (selectedTickets || []).reduce((sum, t) => {
    const qty = parseInt(t.quantity, 10) || 0;
    if (qty <= 0) return sum;
    const qualifies =
      ticketRowIsCastleAndGardenEntry(t) ||
      ticketRowIsGardenAndPlaygroundEntry(t);
    if (!qualifies) return sum;
    if (ticketRowIsHhaHistoricHouseMember(t)) return sum;
    return sum + qty;
  }, 0);
}

/** Step-1 ticket quantity that is not Historic House Member (caps free Animal Park promotion when castle naming differs). */
export function nonHhaMainTicketQty(selectedTickets) {
  return (selectedTickets || []).reduce((sum, t) => {
    if (t.isUpsellItem || t.pageType === 'EventTemplate') return sum;
    const qty = parseInt(t.quantity, 10) || 0;
    if (qty <= 0 || ticketRowIsHhaHistoricHouseMember(t)) return sum;
    return sum + qty;
  }, 0);
}
